import { OFFLINE_STORES, getOfflineStorageDriver } from "./offlineStorageAdapter.js";
import { withVisitaLocalLock } from './visitaLocalFirst.js';
import { canonicalMerge } from './visitaMergeJournal.js';

const QUEUE_CHANGED_EVENT = 'visita-offline-operation-queue-changed';
const DB_NAME = 'coordinacion_visitas_offline_operations';
const DB_VERSION = 2;
const STORE_NAME = 'visita_offline_operations';

let dbPromise = null;

function toNumber(value) {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
}

export function generateOperationUuid() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `op_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function emitQueueChanged() {
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(QUEUE_CHANGED_EVENT));
    }
}

function openDb() {
    if (dbPromise) {
        return dbPromise;
    }

    if (typeof window === 'undefined' || typeof window.indexedDB === 'undefined') {
        dbPromise = Promise.resolve(null);
        return dbPromise;
    }

    dbPromise = new Promise((resolve) => {
        const request = window.indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = () => {
            const db = request.result;
            let store = null;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
                store.createIndex('by_user', 'userId', { unique: false });
                store.createIndex('by_created_at', 'createdAt', { unique: false });
            } else {
                store = request.transaction.objectStore(STORE_NAME);
            }

            if (store) {
                if (!store.indexNames.contains('by_visita')) {
                    store.createIndex('by_visita', 'visitaId', { unique: false });
                }
                if (!store.indexNames.contains('by_status')) {
                    store.createIndex('by_status', 'status', { unique: false });
                }
                if (!store.indexNames.contains('by_uuid')) {
                    store.createIndex('by_uuid', 'operation_uuid', { unique: false });
                }
            }
        };

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => resolve(null);
    });

    return dbPromise;
}

function withStore(db, mode, operation) {
    return new Promise((resolve, reject) => {
        try {
            const transaction = db.transaction(STORE_NAME, mode);
            let resultContainer = undefined;

            transaction.oncomplete = () => {
                resolve(resultContainer);
            };

            transaction.onerror = () => {
                reject(transaction.error || new Error('Error en transacción de outbox'));
            };

            transaction.onabort = () => {
                reject(new Error('Transacción abortada en outbox'));
            };

            const store = transaction.objectStore(STORE_NAME);
            operation(
                store,
                (result) => {
                    resultContainer = result;
                },
                (error) => {
                    try {
                        transaction.abort();
                    } catch {
                        // Ignorar si la transacción ya fue abortada
                    }
                    reject(error);
                },
                transaction
            );
        } catch (error) {
            reject(error);
        }
    });
}

export async function enqueueVisitaOfflineOperation({
    userId,
    visitaId,
    type,
    payload = {},
    revisionId = null,
    baseVersion = null,
    dependencies = [],
    operationUuid = null,
}) {
    const now = new Date().toISOString();
    const effectiveUuid = (typeof operationUuid === 'string' && operationUuid.trim().length > 0)
        ? operationUuid.trim()
        : generateOperationUuid();

    const cleanDependencies = Array.isArray(dependencies)
        ? [...new Set(dependencies.filter((dep) => typeof dep === 'string' && dep.trim().length > 0))]
        : [];

    const clonedPayload = payload && typeof payload === 'object'
        ? JSON.parse(JSON.stringify(payload))
        : {};

    const operation = {
        id: `${toNumber(userId) || 'anon'}-${toNumber(visitaId) || '0'}-${type}-${effectiveUuid}`,
        operation_uuid: effectiveUuid,
        userId: toNumber(userId),
        visitaId: toNumber(visitaId),
        type: String(type || ''),
        payload: Object.freeze(clonedPayload),
        revisionId: revisionId ? String(revisionId).trim() : null,
        base_version: baseVersion !== null && baseVersion !== undefined ? Number(baseVersion) : null,
        dependencies: cleanDependencies,
        status: 'pending',
        retryCount: 0,
        lastError: null,
        createdAt: now,
        updatedAt: now,
    };

    const driver = getOfflineStorageDriver();
    if (type === 'generar_comprobante') {
        if (!operation.userId || !operation.visitaId || clonedPayload.client_uuid !== effectiveUuid) throw new Error('El comprobante requiere su identidad original.');
        if (typeof driver.mutate !== 'function') throw new Error('El almacenamiento no permite conservar el comprobante atómicamente.');
        const saved = await driver.mutate(OFFLINE_STORES.OPERATIONS, operation.id, current => {
            if (current && (current.operation_uuid !== effectiveUuid || current.userId !== operation.userId || current.visitaId !== operation.visitaId
                || current.type !== type || canonicalMerge(current.payload) !== canonicalMerge(clonedPayload))) throw new Error('La intención ya conserva otro contenido. No se reemplaza.');
            return current || operation;
        });
        if (!saved) throw new Error('No se confirmó el guardado del comprobante.');
        emitQueueChanged();
        return saved;
    }
    if (['finalizar_visita', 'iniciar_visita', 'split_work'].includes(type)) {
        if (!operation.userId || !operation.visitaId) throw new Error('El cierre requiere usuario y visita.');
        return withVisitaLocalLock(operation.userId, operation.visitaId, async () => {
            // También se conserva una intención legacy, incluso en conflicto o en vuelo.
            const existing = (await getVisitaOfflineOperationsByVisita(visitaId, userId))
                .find(entry => entry.type === type && entry.status !== 'applied');
            if (existing) return existing;
            operation.id = `${operation.userId}-${operation.visitaId}-${type}`;
            const previousSlot = await driver.get(OFFLINE_STORES.OPERATIONS, operation.id);
            if (previousSlot && ['applied', 'superseded'].includes(previousSlot.status)) operation.id += `-${effectiveUuid}`;
            if (typeof driver.mutate !== 'function') throw new Error('El almacenamiento no permite conservar el cierre de forma atómica.');
            const inserted = await driver.mutate(OFFLINE_STORES.OPERATIONS, operation.id, current => current ? null : operation);
            const saved = inserted || await driver.get(OFFLINE_STORES.OPERATIONS, operation.id);
            if (!saved) throw new Error('No se pudo guardar la intención de cierre.');
            emitQueueChanged();
            return saved;
        });
    }
    try {
        await driver.put(OFFLINE_STORES.OPERATIONS, operation);
        emitQueueChanged();
        return operation;
    } catch (error) {
        if (driver.strict || driver.effectiveEngine === 'sqlite'
            || (typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform?.())) throw error;
        // Compatibilidad web existente; no es una segunda autoridad nativa.
    }

    const db = await openDb();
    if (!db) {
        throw new Error('Almacenamiento no disponible para la outbox de operaciones.');
    }

    await withStore(db, 'readwrite', (store, resolvePut, rejectPut) => {
        const request = store.put(operation);
        request.onsuccess = () => resolvePut(operation);
        request.onerror = () => rejectPut(request.error);
    });

    emitQueueChanged();
    return operation;
}

export async function claimVisitaOfflineOperation({ operationId, ownerId, leaseMs = 60000 }) {
    if (!operationId || !ownerId) {
        return null;
    }

    const driver = getOfflineStorageDriver();
    try {
        if (typeof driver.mutate === 'function') {
            const now = Date.now();
            const claimed = await driver.mutate(OFFLINE_STORES.OPERATIONS, operationId, (entry) => {
                if (!entry) return null;
                const currentStatus = entry.status || 'pending';

                if (currentStatus === 'in_flight') {
                    const expiresAt = entry.leaseExpiresAt !== null && entry.leaseExpiresAt !== undefined ? Number(entry.leaseExpiresAt) : ((Date.parse(entry.inFlightAt || '') || 0) + leaseMs);
                    if (now < expiresAt) {
                        return null; // Concesión activa
                    }
                } else if (['applied', 'conflict', 'superseded'].includes(currentStatus)) {
                    return null;
                }

                const claimToken = generateOperationUuid();
                return {
                    ...entry,
                    status: 'in_flight',
                    ownerId,
                    claimToken,
                    success: true,
                    inFlightAt: new Date(now).toISOString(),
                    leaseExpiresAt: now + leaseMs,
                    updatedAt: new Date(now).toISOString(),
                };
            });

            if (claimed) {
                emitQueueChanged();
                return claimed;
            }
            if (driver?.strict || driver?.effectiveEngine === 'sqlite' || (typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform?.())) {
                return null;
            }
            return null;
        }
    } catch (driverError) {
        if (driver?.strict || driver?.effectiveEngine === 'sqlite' || (typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform?.())) {
            console.error('[visitaOfflineOperationQueue] Error crítico en SQLite al reclamar operación; prohibido degradar a IndexedDB:', driverError);
            throw driverError;
        }
    }

    const db = await openDb();
    if (!db) {
        return null;
    }

    const claimed = await withStore(db, 'readwrite', (store, resolve, reject) => {
        const getRequest = store.get(operationId);
        getRequest.onerror = () => reject(getRequest.error);
        getRequest.onsuccess = () => {
            const entry = getRequest.result;
            if (!entry) {
                resolve(null);
                return;
            }

            const now = Date.now();
            const currentStatus = entry.status || 'pending';

            if (currentStatus === 'in_flight') {
                const expiresAt = entry.leaseExpiresAt !== null && entry.leaseExpiresAt !== undefined ? Number(entry.leaseExpiresAt) : ((Date.parse(entry.inFlightAt || '') || 0) + leaseMs);
                // Concesión activa: ningún emisor (ni siquiera el mismo ownerId en otra llamada simultánea)
                // puede adquirirla mientras esté en vuelo activa. La exclusión es absoluta.
                if (now < expiresAt) {
                    resolve(null);
                    return;
                }
            } else if (['applied', 'conflict', 'superseded'].includes(currentStatus)) {
                resolve(null);
                return;
            }

            const claimToken = generateOperationUuid();
            const modified = {
                ...entry,
                status: 'in_flight',
                ownerId,
                claimToken,
                success: true,
                inFlightAt: new Date(now).toISOString(),
                leaseExpiresAt: now + leaseMs,
                updatedAt: new Date(now).toISOString(),
            };

            const putRequest = store.put(modified);
            putRequest.onsuccess = () => resolve(modified);
            putRequest.onerror = () => reject(putRequest.error);
        };
    });

    if (claimed) {
        emitQueueChanged();
    }
    return claimed;
}

export async function renewVisitaOfflineOperationLease({ operationId, ownerId, claimToken, leaseMs = 60000 }) {
    if (!operationId || !ownerId || !claimToken) {
        return null;
    }

    const driver = getOfflineStorageDriver();
    try {
        if (typeof driver.mutate === 'function') {
            const now = Date.now();
            const renewed = await driver.mutate(OFFLINE_STORES.OPERATIONS, operationId, (entry) => {
                if (!entry || entry.status !== 'in_flight') {
                    return null;
                }
                if (entry.ownerId !== ownerId || entry.claimToken !== claimToken) {
                    return null;
                }
                return {
                    ...entry,
                    leaseExpiresAt: now + leaseMs,
                    updatedAt: new Date(now).toISOString(),
                };
            });

            if (renewed) {
                emitQueueChanged();
                return renewed;
            }
            return null;
        }
    } catch (error) {
        if (driver.strict || driver.effectiveEngine === 'sqlite'
            || (typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform?.())) throw error;
    }

    const db = await openDb();
    if (!db) {
        return null;
    }

    const renewed = await withStore(db, 'readwrite', (store, resolve, reject) => {
        const getRequest = store.get(operationId);
        getRequest.onerror = () => reject(getRequest.error);
        getRequest.onsuccess = () => {
            const entry = getRequest.result;
            if (!entry || entry.status !== 'in_flight') {
                resolve(null);
                return;
            }

            if (entry.ownerId !== ownerId || entry.claimToken !== claimToken) {
                resolve(null);
                return;
            }

            const now = Date.now();
            const modified = {
                ...entry,
                leaseExpiresAt: now + leaseMs,
                updatedAt: new Date(now).toISOString(),
            };

            const putRequest = store.put(modified);
            putRequest.onsuccess = () => resolve(modified);
            putRequest.onerror = () => reject(putRequest.error);
        };
    });

    if (renewed) {
        emitQueueChanged();
    }
    return renewed;
}

export async function recoverExpiredInFlightOperations(options = {}) {
    const maxLeaseMs = typeof options === 'number' ? options : (options?.maxLeaseMs || 60000);
    const filterUserId = typeof options === 'object' && options?.userId ? toNumber(options.userId) : null;
    const now = Date.now();

    const driver = getOfflineStorageDriver();
    try {
        const allOps = await driver.getAll(OFFLINE_STORES.OPERATIONS, null, { userId: filterUserId });
        if (Array.isArray(allOps)) {
            const toUpdate = [];

            for (const op of allOps) {
                if (op?.status === 'in_flight') {
                    if (filterUserId !== null && toNumber(op.userId) !== filterUserId) {
                        continue;
                    }

                    const inFlightTime = Date.parse(op.inFlightAt || op.updatedAt || op.createdAt || '') || 0;
                    const leaseExpiresAt = op.leaseExpiresAt !== null && op.leaseExpiresAt !== undefined ? Number(op.leaseExpiresAt) : (inFlightTime + maxLeaseMs);

                    if (now >= leaseExpiresAt) {
                        const updatedOp = await driver.mutate(OFFLINE_STORES.OPERATIONS, op.id, current => {
                            if (!current || current.status !== 'in_flight' || current.claimToken !== op.claimToken
                                || current.ownerId !== op.ownerId || current.leaseExpiresAt !== op.leaseExpiresAt) return null;
                            return {
                                ...current, status: 'pending', ownerId: null, claimToken: null,
                                // Conservar la evidencia de que este comando pudo llegar al servidor.
                                inFlightAt: current.inFlightAt || current.updatedAt || current.createdAt,
                                leaseExpiresAt: null,
                                lastError: 'Bloqueo in_flight expirado; recuperado para reintento.',
                                updatedAt: new Date().toISOString(),
                            };
                        });
                        if (updatedOp) toUpdate.push(updatedOp);
                    }
                }
            }

            if (toUpdate.length > 0) {
                emitQueueChanged();
            }
            return toUpdate;
        }
        if (driver.strict || driver.effectiveEngine === 'sqlite') return [];
    } catch (error) {
        if (driver?.strict || driver?.effectiveEngine === 'sqlite' || (typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform?.())) throw error;
    }

    const db = await openDb();
    if (!db) {
        return [];
    }

    const recovered = await withStore(db, 'readwrite', (store, resolve, reject) => {
        const request = store.getAll();
        request.onerror = () => reject(request.error);
        request.onsuccess = () => {
            const list = Array.isArray(request.result) ? request.result : [];
            const toUpdate = [];

            for (const op of list) {
                if (op?.status === 'in_flight') {
                    if (filterUserId !== null && toNumber(op.userId) !== filterUserId) {
                        continue;
                    }

                    const inFlightTime = Date.parse(op.inFlightAt || op.updatedAt || op.createdAt || '') || 0;
                    const leaseExpiresAt = op.leaseExpiresAt !== null && op.leaseExpiresAt !== undefined ? Number(op.leaseExpiresAt) : (inFlightTime + maxLeaseMs);

                    if (now >= leaseExpiresAt) {
                        const updatedOp = {
                            ...op,
                            status: 'pending',
                            ownerId: null,
                            claimToken: null,
                            inFlightAt: op.inFlightAt || op.updatedAt || op.createdAt,
                            leaseExpiresAt: null,
                            lastError: 'Bloqueo in_flight expirado; recuperado para reintento.',
                            updatedAt: new Date().toISOString(),
                        };
                        store.put(updatedOp);
                        toUpdate.push(updatedOp);
                    }
                }
            }
            resolve(toUpdate);
        };
    });

    if (recovered.length > 0) {
        emitQueueChanged();
    }
    return recovered;
}

export async function getVisitaOfflineOperationsByUser(userId, { visitaId } = {}) {
    const normalizedUserId = toNumber(userId);

    const driver = getOfflineStorageDriver();
    try {
        const driverOps = await driver.getAll(OFFLINE_STORES.OPERATIONS, null, { userId: normalizedUserId, ...(visitaId===undefined?{}:{visitaId}) });
        if (Array.isArray(driverOps)) {
            const normalizedList = [];

            for (const op of driverOps) {
                const opUserId = toNumber(op?.userId);
                if (normalizedUserId !== null && opUserId !== normalizedUserId) {
                    continue;
                }
                if(visitaId!==undefined&&toNumber(op?.visitaId)!==visitaId)continue;

                const operationUuid = op?.operation_uuid || op?.uuid || (op?.id ? String(op.id) : generateOperationUuid());
                const dependencies = Array.isArray(op?.dependencies) ? op.dependencies : [];
                const status = op?.status || 'pending';
                if (status === 'superseded') continue;

                normalizedList.push({
                    ...op,
                    operation_uuid: operationUuid,
                    dependencies,
                    status,
                    retryCount: Number(op?.retryCount) || 0,
                });
            }

            normalizedList.sort((left, right) => {
                const leftTime = Date.parse(left?.createdAt || '') || 0;
                const rightTime = Date.parse(right?.createdAt || '') || 0;
                if (leftTime !== rightTime) {
                    return leftTime - rightTime;
                }
                return String(left?.id || left?.operation_uuid || '').localeCompare(String(right?.id || right?.operation_uuid || ''));
            });

            return normalizedList;
        }
    } catch (driverError) {
        if (driver?.strict || driver?.effectiveEngine === 'sqlite' || (typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform?.())) {
            console.error('[visitaOfflineOperationQueue] Error crítico en SQLite al leer operaciones; prohibido degradar a IndexedDB:', driverError);
            throw driverError;
        }
    }

    const db = await openDb();
    if (!db) {
        return [];
    }

    // Normalización transaccional y atómica: si existen filas legacy,
    // se leen y actualizan dentro de una misma transacción readwrite verificando existencia
    const operations = await withStore(db, 'readwrite', (store, resolve, reject) => {
        const getAllReq = store.getAll();
        getAllReq.onerror = () => reject(getAllReq.error);
        getAllReq.onsuccess = () => {
            const list = Array.isArray(getAllReq.result) ? getAllReq.result : [];
            const normalizedList = [];

            for (const op of list) {
                const opUserId = toNumber(op?.userId);
                if (normalizedUserId !== null && opUserId !== normalizedUserId) {
                    continue;
                }

                // Preservar clave histórica: si no tiene operation_uuid, usar String(op.id)
                // para que coincida exactamente con la clave histórica operation:${id} enviada previamente
                const hasPersistedUuid = Boolean(op?.operation_uuid);
                const operationUuid = op?.operation_uuid || op?.uuid || (op?.id ? String(op.id) : generateOperationUuid());
                const dependencies = Array.isArray(op?.dependencies) ? op.dependencies : [];
                const status = op?.status || 'pending';
                if (status === 'superseded') continue;

                const normalizedOp = {
                    ...op,
                    operation_uuid: operationUuid,
                    dependencies,
                    status,
                    retryCount: Number(op?.retryCount) || 0,
                };

                if (!hasPersistedUuid && op?.id) {
                    // Actualización in-place verificando que no se sobreescriban datos concurrentes
                    store.put(normalizedOp);
                }

                normalizedList.push(normalizedOp);
            }

            normalizedList.sort((left, right) => {
                const leftTime = Date.parse(left?.createdAt || '') || 0;
                const rightTime = Date.parse(right?.createdAt || '') || 0;
                if (leftTime !== rightTime) {
                    return leftTime - rightTime;
                }
                return String(left?.id || left?.operation_uuid || '').localeCompare(String(right?.id || right?.operation_uuid || ''));
            });

            resolve(normalizedList);
        };
    });

    return operations;
}

export async function getVisitaOfflineOperationsByVisita(visitaId, userId = null) {
    if (!userId) {
        return [];
    }
    const targetVisitaId = toNumber(visitaId);
    if(!Number.isSafeInteger(targetVisitaId)||targetVisitaId<1)return [];
    const operations = await getVisitaOfflineOperationsByUser(userId,{visitaId:targetVisitaId});
    return operations.filter((op) => toNumber(op.visitaId) === targetVisitaId);
}

export async function updateVisitaOfflineOperationStatus(id, {
    status,
    lastError = null,
    incrementRetry = false,
    leaseExpiresAt = undefined,
    inFlightAt = undefined,
    ownerId = undefined,
    claimToken = undefined,
    payload = undefined,
    operationUuid = undefined,
}) {
    if (!id) {
        return null;
    }

    const driver = getOfflineStorageDriver();
    const mayUpdate = entry => {
        if (!entry) return false;
        if (['applied', 'superseded'].includes(entry.status)) return false;
        if (claimToken !== undefined && entry.claimToken !== claimToken) return false;
        if (ownerId !== undefined && entry.ownerId !== ownerId) return false;
        if (entry.type === 'finalizar_visita' && entry.status === 'in_flight'
            && (!claimToken || !ownerId || claimToken !== entry.claimToken || ownerId !== entry.ownerId)) return false;
        // El setter de estados no es un reconciliador de comandos transmitidos.
        if ((entry.type === 'finalizar_visita' || entry.inFlightAt || entry.in_flight_at)
            && (payload !== undefined || operationUuid !== undefined)) return false;
        return true;
    };
    try {
        if (typeof driver.mutate === 'function') {
            const updated = await driver.mutate(OFFLINE_STORES.OPERATIONS, id, (entry) => {
                if (!mayUpdate(entry)) return null;

                const nextRetry = incrementRetry ? (Number(entry.retryCount) || 0) + 1 : (Number(entry.retryCount) || 0);
                return {
                    ...entry,
                    status: status || entry.status,
                    lastError: lastError !== undefined ? lastError : entry.lastError,
                    retryCount: nextRetry,
                    updatedAt: new Date().toISOString(),
                    ...(payload !== undefined ? { payload: Object.freeze(JSON.parse(JSON.stringify(payload))) } : {}),
                    ...(operationUuid !== undefined ? { operationUuid } : {}),
                    ...(status === 'in_flight' ? { inFlightAt: inFlightAt !== undefined ? inFlightAt : new Date().toISOString() } : {}),
                    ...(leaseExpiresAt !== undefined ? { leaseExpiresAt } : {}),
                    ...(ownerId !== undefined ? { ownerId } : {}),
                };
            });

            if (updated) {
                emitQueueChanged();
                return updated;
            }
            return null;
        }
    } catch (error) {
        if (driver?.strict || driver?.effectiveEngine === 'sqlite' || (typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform?.())) throw error;
    }

    const db = await openDb();
    if (!db) {
        return null;
    }

    const updated = await withStore(db, 'readwrite', (store, resolveUpdate, rejectUpdate) => {
        const getRequest = store.get(id);
        getRequest.onerror = () => rejectUpdate(getRequest.error);
        getRequest.onsuccess = () => {
            const entry = getRequest.result;
            if (!mayUpdate(entry)) {
                resolveUpdate(null);
                return;
            }

            const nextRetry = incrementRetry ? (Number(entry.retryCount) || 0) + 1 : (Number(entry.retryCount) || 0);
            const modified = {
                ...entry,
                status: status || entry.status,
                lastError: lastError !== undefined ? lastError : entry.lastError,
                retryCount: nextRetry,
                updatedAt: new Date().toISOString(),
                ...(payload !== undefined ? { payload: Object.freeze(JSON.parse(JSON.stringify(payload))) } : {}),
                ...(operationUuid !== undefined ? { operationUuid } : {}),
                ...(status === 'in_flight' ? { inFlightAt: inFlightAt !== undefined ? inFlightAt : new Date().toISOString() } : {}),
                ...(leaseExpiresAt !== undefined ? { leaseExpiresAt } : {}),
                ...(ownerId !== undefined ? { ownerId } : {}),
            };

            const putRequest = store.put(modified);
            putRequest.onsuccess = () => resolveUpdate(modified);
            putRequest.onerror = () => rejectUpdate(putRequest.error);
        };
    });

    if (updated) {
        emitQueueChanged();
    }
    return updated;
}

export async function removeVisitaOfflineOperation(id, { ownerId = undefined, claimToken = undefined } = {}) {
    if (!id) {
        return false;
    }

    const driver = getOfflineStorageDriver();
    const mayRemove = entry => {
        if (!entry) return false;
        if (entry.status === 'superseded') return false;
        const protectedEntry = entry.status === 'in_flight'
            || (entry.type === 'finalizar_visita' && (entry.inFlightAt || entry.in_flight_at));
        if (protectedEntry && (!ownerId || !claimToken)) return false;
        if (claimToken !== undefined && (entry.claimToken !== claimToken || entry.status !== 'in_flight')) return false;
        if (ownerId !== undefined && entry.ownerId !== ownerId) return false;
        return true;
    };
    try {
        if (typeof driver.compareAndDelete === 'function') {
            const deleted = await driver.compareAndDelete(OFFLINE_STORES.OPERATIONS, id, (entry) => {
                return mayRemove(entry);
            });

            if (deleted) {
                emitQueueChanged();
                return true;
            }
            if (driver?.strict || driver?.effectiveEngine === 'sqlite' || (typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform?.())) {
                return false;
            }
            return false;
        }
    } catch (driverError) {
        if (driver?.strict || driver?.effectiveEngine === 'sqlite' || (typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform?.())) {
            console.error('[visitaOfflineOperationQueue] Error crítico en SQLite al eliminar operación; prohibido degradar a IndexedDB:', driverError);
            throw driverError;
        }
    }

    const db = await openDb();
    if (!db) {
        return false;
    }

    const removed = await withStore(db, 'readwrite', (store, resolveDelete, rejectDelete) => {
        const getRequest = store.get(id);
        getRequest.onerror = () => rejectDelete(getRequest.error);
        getRequest.onsuccess = () => {
            const entry = getRequest.result;
            if (!mayRemove(entry)) {
                resolveDelete(false);
                return;
            }

            const deleteRequest = store.delete(id);
            deleteRequest.onsuccess = () => resolveDelete(true);
            deleteRequest.onerror = () => rejectDelete(deleteRequest.error);
        };
    });

    if (removed) {
        emitQueueChanged();
    }
    return Boolean(removed);
}

export async function acknowledgeReconciledFinalization(original, receipt) {
    if (receipt?.status !== 'applied' || receipt.applied_operation_uuid !== original.operation_uuid
        || Number(receipt.visita_id) !== Number(original.visitaId)) throw new Error('El recibo no corresponde a este cierre.');
    const driver = getOfflineStorageDriver();
    if (original.payload?.finalization_contract === 2) {
        const committed = await driver.commitVisitaFinalization({ ...original, reconciledReceipt: true },
            { ...receipt, empresa_id: original.payload.owner_empresa_id, responses_version: original.payload.expected_version });
        if (committed) {
            emitQueueChanged();
            if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('visita-finalization-acknowledged', { detail: { userId: original.userId, visitaId: original.visitaId } }));
        }
        return committed ? driver.get(OFFLINE_STORES.OPERATIONS, original.id) : null;
    }
    const saved = await driver.mutate(OFFLINE_STORES.OPERATIONS, original.id, current => {
        if (!current || current.status === 'in_flight' || current.status === 'superseded'
            || current.userId !== original.userId || current.operation_uuid !== original.operation_uuid
            || Number(current.visitaId) !== Number(original.visitaId)
            || JSON.stringify(current.payload) !== JSON.stringify(original.payload)) return null;
        return { ...current, status: 'applied', updatedAt: new Date().toISOString(), lastError: null };
    });
    if (saved) emitQueueChanged();
    return saved;
}

export async function publishVisitaFinalizationSuccessor({ original, payload, authorization, dependencies = [], operationUuid = generateOperationUuid() }) {
    if (original?.type !== 'finalizar_visita' || original.status !== 'conflict'
        || authorization?.status !== 'replacement_authorized' || authorization.original_uuid !== original.operation_uuid
        || !authorization.replacement_token || Number(payload.expected_version) !== Number(authorization.current_version)) {
        throw new Error('El cierre requiere una conciliación vigente antes de reemplazarlo.');
    }
    const nextPayload = JSON.parse(JSON.stringify({ ...payload, finalization_contract: 2,
        supersedes_client_uuid: original.operation_uuid, replacement_token: authorization.replacement_token }));
    const now = new Date().toISOString();
    const successor = { id: `${original.userId}-${original.visitaId}-finalizar_visita-${operationUuid}`,
        userId: original.userId, visitaId: original.visitaId, type: 'finalizar_visita', operation_uuid: operationUuid,
        payload: nextPayload, dependencies: [...dependencies], status: 'pending', retryCount: 0, createdAt: now, updatedAt: now };
    const driver = getOfflineStorageDriver();
    return withVisitaLocalLock(original.userId, original.visitaId, async () => {
        if (!driver.publishFinalizationSuccessor) throw new Error('El almacenamiento no permite guardar el reemplazo completo.');
        const saved = await driver.publishFinalizationSuccessor(original, successor);
        if (!saved) throw new Error('El cierre cambió durante la conciliación. Se conserva la intención original.');
        emitQueueChanged();
        return saved;
    });
}

export function areOperationDependenciesResolved(operation, { activeDrafts = [], activeMedia = [], allOperations = [] }) {
    if (!operation) {
        return false;
    }

    const targetVisitaId = toNumber(operation.visitaId);
    const opDependencies = Array.isArray(operation.dependencies) ? operation.dependencies : [];

    // 1. Barrera de visita para finalización:
    // Si la operación es finalizar_visita o tiene dependencias declaradas hacia un borrador:
    // Si existe CUALQUIER borrador activo para esta visita, la operación está bloqueada.
    // Esto previene que si el borrador A fue reemplazado por el borrador B, la finalización se escape antes de tiempo.
    const hasAnyDraftForSameVisita = targetVisitaId !== null && activeDrafts.some((d) => {
        const draftVisitaId = toNumber(d?.visitaId);
        if (draftVisitaId !== null && draftVisitaId === targetVisitaId) {
            return true;
        }
        if (typeof d?.key === 'string' && d.key.endsWith(`:${targetVisitaId}`)) {
            return true;
        }
        return false;
    });

    if (hasAnyDraftForSameVisita) {
        const dependsOnDraft = operation.type === 'finalizar_visita' ||
            opDependencies.some((dep) => dep.startsWith('draft:') || dep.startsWith('rev_') || dep.startsWith('rev-') || dep === `draft:visita_${targetVisitaId}`);
        if (dependsOnDraft) {
            return false;
        }
    }

    // 2. Barrera de fotos para finalización:
    // Si la operación es finalizar_visita y existen fotos activas para la misma visita, retenerla
    const hasAnyMediaForSameVisita = targetVisitaId !== null && activeMedia.some((m) => {
        const mediaVisitaId = toNumber(m?.visitaId);
        return mediaVisitaId !== null && mediaVisitaId === targetVisitaId;
    });

    if (hasAnyMediaForSameVisita && operation.type === 'finalizar_visita') {
        return false;
    }

    // 3. Barrera de operaciones previas no resueltas de la misma visita:
    // Las operaciones sobre la misma visita deben resolverse en estricto orden secuencial.
    if (targetVisitaId !== null && Array.isArray(allOperations)) {
        const priorUnappliedOp = allOperations.find((op) => {
            if (!op || op.id === operation.id || op.operation_uuid === operation.operation_uuid) {
                return false;
            }
            if (toNumber(op.visitaId) !== targetVisitaId) {
                return false;
            }
            if (op.status === 'applied') {
                return false;
            }
            if (op.type === 'finalizar_visita' && operation.type !== 'finalizar_visita') return false;
            // Si la operación previa es una finalización de visita y la actual también lo es,
            // no bloquear: la actual es un reemplazo/reintento fresco que supersedió a la previa.
            if (op.type === 'finalizar_visita' && operation.type === 'finalizar_visita') {
                return false;
            }
            const opCreated = Date.parse(op.createdAt || '') || 0;
            const currentCreated = Date.parse(operation.createdAt || '') || 0;
            if (opCreated !== currentCreated) {
                return opCreated < currentCreated;
            }
            const opIdStr = String(op?.id || op?.operation_uuid || '');
            const curIdStr = String(operation?.id || operation?.operation_uuid || '');
            return opIdStr.localeCompare(curIdStr) < 0;
        });

        if (priorUnappliedOp) {
            return false;
        }
    }

    // 4. Verificación exhaustiva de dependencias explícitas
    if (opDependencies.length === 0) {
        return true;
    }

    const pendingDraftKeys = new Set(
        activeDrafts.flatMap((d) => [
            d?.revisionId,
            d?.key,
            d?.visitaId ? `draft:visita_${d.visitaId}` : null,
            d?.visitaId ? `draft:visita_${d.visitaId}_legacy` : null,
        ]).filter(Boolean)
    );

    const pendingMediaIds = new Set(
        activeMedia.flatMap((m) => [m?.id, m?.queueId]).filter(Boolean)
    );

    const pendingOpUuids = new Set(
        allOperations
            .filter((op) => op?.id !== operation.id && op?.operation_uuid !== operation.operation_uuid && op?.status !== 'applied')
            // Ignorar operaciones de finalizar_visita previas si la actual es finalizar_visita
            .filter((op) => !(operation.type === 'finalizar_visita' && op?.type === 'finalizar_visita'))
            .flatMap((op) => [op?.id, op?.operation_uuid])
            .filter(Boolean)
    );

    for (const dep of opDependencies) {
        if (pendingDraftKeys.has(dep)) {
            return false;
        }
        if (pendingMediaIds.has(dep)) {
            return false;
        }
        if (pendingOpUuids.has(dep)) {
            return false;
        }
    }

    return true;
}

export async function getOfflineOperationPendingCountByUser(userId) {
    const operations = await getVisitaOfflineOperationsByUser(userId);
    return operations.filter((op) => op.status !== 'applied').length;
}

export function subscribeOfflineOperationQueueChanges(listener) {
    if (typeof window === 'undefined' || typeof listener !== 'function') {
        return () => {};
    }

    window.addEventListener(QUEUE_CHANGED_EVENT, listener);
    return () => window.removeEventListener(QUEUE_CHANGED_EVENT, listener);
}

